import { Environments } from 'src/configurations'
import { ReceiptAttachmentResponse } from 'src/models/receiptAttachment.models'
import {
  ReceiptRecoveryCase,
  ReceiptRecoveryDisplayCase,
  ReceiptRecoveryLifecycleState,
} from 'src/models/transaction.models'

import { ReceiptEvidenceAttachmentPreviewType } from './receiptEvidence'

interface GetReceiptRecoveryCaseForDisplayParams {
  enabled: boolean
  mockDataEnabled: boolean
  receiptRecoveryCase?: ReceiptRecoveryCase
  receiptAttachments?: ReceiptAttachmentResponse[]
  localPreviewState?: ReceiptRecoveryLifecycleState
}

interface CanSubmitLiveReceiptRecoveryReviewParams {
  isMockPreview: boolean
  organizationId: string | undefined
  receiptRecoveryCase: ReceiptRecoveryDisplayCase | undefined
  receiptAttachments: ReceiptAttachmentResponse[]
}

export const mergeReceiptRecoveryAttachments = <T extends { id: string }>(
  receiptAttachments: readonly T[],
  selectedReceipt?: T,
): T[] => {
  if (
    selectedReceipt === undefined ||
    receiptAttachments.some(({ id }) => id === selectedReceipt.id)
  ) {
    return [...receiptAttachments]
  }

  return [selectedReceipt, ...receiptAttachments]
}

export const isReceiptEvidenceReviewCase = (
  receiptRecoveryCase: ReceiptRecoveryCase | undefined,
): boolean =>
  receiptRecoveryCase?.lifecycleState ===
    ReceiptRecoveryLifecycleState.NeedsReview ||
  receiptRecoveryCase?.reviewerDecision !== undefined ||
  receiptRecoveryCase?.status === 'REVIEW_REJECTED' ||
  (receiptRecoveryCase?.lifecycleState ===
    ReceiptRecoveryLifecycleState.ReceiptReconciled &&
    receiptRecoveryCase.reconciledByDisplayName !== undefined)

export const hasManualAttachmentProvenance = (
  receiptRecoveryCase: ReceiptRecoveryCase | undefined,
): boolean =>
  Boolean(receiptRecoveryCase?.attachedByDisplayName?.trim()) ||
  Boolean(receiptRecoveryCase?.attachedAt)

export const getReviewedReceiptAttachmentId = (
  receiptRecoveryCase: ReceiptRecoveryCase | undefined,
): string | undefined => {
  if (receiptRecoveryCase?.reviewedReceiptAttachmentId !== undefined) {
    return receiptRecoveryCase.reviewedReceiptAttachmentId
  }

  return hasManualAttachmentProvenance(receiptRecoveryCase)
    ? undefined
    : receiptRecoveryCase?.receiptAttachmentId
}

export const receiptRecoveryPreviewSearchParam = 'receiptRecoveryPreview'
export const receiptEvidenceAttachmentPreviewSearchParam =
  'receiptEvidenceAttachmentPreview'

export const receiptRecoveryMockPreviewStates = [
  'waiting-on-spender',
  'missing-receipt',
  'needs-review',
  'receipt-reconciled',
  'receipt-rejected',
  'receipt-reconciled-compact',
] as const

export type ReceiptRecoveryMockPreviewState =
  (typeof receiptRecoveryMockPreviewStates)[number]

export const defaultReceiptRecoveryMockPreviewState: ReceiptRecoveryMockPreviewState =
  'needs-review'

const localPreviewStateByName: Record<
  ReceiptRecoveryMockPreviewState,
  ReceiptRecoveryLifecycleState
> = {
  'waiting-on-spender': ReceiptRecoveryLifecycleState.WaitingOnSpender,
  'missing-receipt': ReceiptRecoveryLifecycleState.MissingReceipt,
  'needs-review': ReceiptRecoveryLifecycleState.NeedsReview,
  'receipt-reconciled': ReceiptRecoveryLifecycleState.ReceiptReconciled,
  'receipt-rejected': ReceiptRecoveryLifecycleState.MissingReceipt,
  'receipt-reconciled-compact': ReceiptRecoveryLifecycleState.ReceiptReconciled,
}

const isReceiptRecoveryMockPreviewState = (
  state: string,
): state is ReceiptRecoveryMockPreviewState =>
  receiptRecoveryMockPreviewStates.some(
    (previewState) => previewState === state,
  )

const localAttachmentPreviewTypeByName: Record<
  string,
  ReceiptEvidenceAttachmentPreviewType
> = {
  image: 'image',
  pdf: 'pdf',
  'in-app-upload': 'in-app-upload',
  'transaction-upload': 'transaction-upload',
  email: 'email',
  'phone-message': 'phone-message',
}

const recoveredDocumentPreview = {
  recoveredFileName: 'ACME_HOTEL_0418.pdf',
  recoveredFrom: 'GMAIL' as const,
  recoveredReceipt: {
    merchantName: 'ACME Hotel & Restaurant',
    submittedAt: '2026-04-22T12:14:00Z',
  },
}

export const getReceiptRecoveryCaseForMockPreviewState = (
  previewState: ReceiptRecoveryMockPreviewState,
  recoveredAttachmentId?: string,
): ReceiptRecoveryDisplayCase =>
  withRecoveredAttachmentId(
    previewState === 'receipt-rejected'
      ? getLocalRejectedPreviewCase()
      : getLocalPreviewCase(localPreviewStateByName[previewState]),
    recoveredAttachmentId,
  )

const withRecoveredAttachmentId = (
  receiptRecoveryCase: ReceiptRecoveryDisplayCase,
  recoveredAttachmentId?: string,
): ReceiptRecoveryDisplayCase =>
  recoveredAttachmentId &&
  (receiptRecoveryCase.lifecycleState ===
    ReceiptRecoveryLifecycleState.NeedsReview ||
    receiptRecoveryCase.lifecycleState ===
      ReceiptRecoveryLifecycleState.ReceiptReconciled ||
    receiptRecoveryCase.status === 'REVIEW_REJECTED')
    ? { ...receiptRecoveryCase, recoveredAttachmentId }
    : receiptRecoveryCase

const getLocalPreviewCase = (
  lifecycleState: ReceiptRecoveryLifecycleState,
): ReceiptRecoveryDisplayCase => {
  switch (lifecycleState) {
    case ReceiptRecoveryLifecycleState.WaitingOnSpender:
      return {
        lifecycleState,
        lastAgentRunAt: '2026-04-22T12:14:00Z',
      }
    case ReceiptRecoveryLifecycleState.MissingReceipt:
      return {
        lifecycleState,
        lastAgentRunAt: '2026-04-22T12:14:00Z',
      }
    case ReceiptRecoveryLifecycleState.NeedsReview:
      return {
        lifecycleState,
        lastAgentRunAt: '2026-04-22T12:14:00Z',
        ...recoveredDocumentPreview,
      }
    case ReceiptRecoveryLifecycleState.ReceiptReconciled:
      return {
        lifecycleState,
        lastAgentRunAt: '2026-04-22T12:14:00Z',
        ...recoveredDocumentPreview,
        status: 'MATCHED',
        reviewerDecision: 'APPROVE',
        reviewedByDisplayName: 'Local preview',
        reviewedAt: '2026-04-23T13:12:00Z',
        reconciledByDisplayName: 'Local preview',
        reconciledAt: '2026-04-23T13:12:00Z',
      }
  }
}

const getLocalRejectedPreviewCase = (): ReceiptRecoveryDisplayCase => ({
  lifecycleState: ReceiptRecoveryLifecycleState.MissingReceipt,
  lastAgentRunAt: '2026-04-22T12:14:00Z',
  ...recoveredDocumentPreview,
  status: 'REVIEW_REJECTED',
  reviewerDecision: 'REJECT',
  reviewedByDisplayName: 'Local preview',
  reviewedAt: '2026-04-23T13:12:00Z',
})

export const getReceiptRecoveryMockPreviewStateFromSearch = (
  search: string,
): ReceiptRecoveryMockPreviewState | undefined => {
  const previewState =
    new URLSearchParams(search).get(receiptRecoveryPreviewSearchParam) ?? ''

  return isReceiptRecoveryMockPreviewState(previewState)
    ? previewState
    : undefined
}

export const getReceiptRecoveryPreviewStateFromSearch = (
  search: string,
): ReceiptRecoveryLifecycleState | undefined => {
  const previewState = getReceiptRecoveryMockPreviewStateFromSearch(search)

  return previewState ? localPreviewStateByName[previewState] : undefined
}

const getAdjacentReceiptRecoveryMockPreviewState = (
  previewState: ReceiptRecoveryMockPreviewState,
  direction: 1 | -1,
): ReceiptRecoveryMockPreviewState => {
  const currentIndex = receiptRecoveryMockPreviewStates.indexOf(previewState)
  const nextIndex =
    (currentIndex + direction + receiptRecoveryMockPreviewStates.length) %
    receiptRecoveryMockPreviewStates.length

  return receiptRecoveryMockPreviewStates[nextIndex]
}

export const getNextReceiptRecoveryMockPreviewState = (
  previewState: ReceiptRecoveryMockPreviewState,
): ReceiptRecoveryMockPreviewState =>
  getAdjacentReceiptRecoveryMockPreviewState(previewState, 1)

export const getPreviousReceiptRecoveryMockPreviewState = (
  previewState: ReceiptRecoveryMockPreviewState,
): ReceiptRecoveryMockPreviewState =>
  getAdjacentReceiptRecoveryMockPreviewState(previewState, -1)

export const getReceiptEvidenceAttachmentPreviewTypeFromSearch = (
  search: string,
): ReceiptEvidenceAttachmentPreviewType | undefined =>
  localAttachmentPreviewTypeByName[
    new URLSearchParams(search).get(
      receiptEvidenceAttachmentPreviewSearchParam,
    ) ?? ''
  ]

interface GetLocalReceiptRecoveryPreviewStateParams {
  enabled: boolean
  mockDataEnabled: boolean
  environment: Environments
  search: string
}

export const getLocalReceiptRecoveryPreviewState = ({
  enabled,
  mockDataEnabled,
  environment,
  search,
}: GetLocalReceiptRecoveryPreviewStateParams):
  | ReceiptRecoveryLifecycleState
  | undefined => {
  if (
    !enabled ||
    !mockDataEnabled ||
    (environment !== Environments.Dev && environment !== Environments.Stage)
  ) {
    return undefined
  }

  return (
    getReceiptRecoveryPreviewStateFromSearch(search) ??
    ReceiptRecoveryLifecycleState.NeedsReview
  )
}

export const getLocalReceiptEvidenceAttachmentPreviewType = ({
  enabled,
  mockDataEnabled,
  environment,
  search,
}: GetLocalReceiptRecoveryPreviewStateParams):
  | ReceiptEvidenceAttachmentPreviewType
  | undefined => {
  if (!enabled || !mockDataEnabled || environment !== Environments.Dev) {
    return undefined
  }

  return getReceiptEvidenceAttachmentPreviewTypeFromSearch(search)
}

export const getReceiptRecoveryCaseForDisplay = ({
  enabled,
  mockDataEnabled,
  receiptRecoveryCase,
  receiptAttachments,
  localPreviewState,
}: GetReceiptRecoveryCaseForDisplayParams):
  | ReceiptRecoveryDisplayCase
  | undefined => {
  if (!enabled) return undefined
  if (receiptRecoveryCase) {
    if (receiptRecoveryCase.lifecycleState) {
      return receiptRecoveryCase as ReceiptRecoveryDisplayCase
    }

    const lifecycleState =
      receiptRecoveryLifecycleStateByStatus[receiptRecoveryCase.status ?? '']
    if (!lifecycleState) return undefined

    const reviewedReceiptAttachmentId =
      getReviewedReceiptAttachmentId(receiptRecoveryCase)
    const receiptAttachment = receiptAttachments?.find(
      (attachment) => attachment.id === reviewedReceiptAttachmentId,
    )

    return {
      ...receiptRecoveryCase,
      lifecycleState,
      lastAgentRunAt: receiptRecoveryCase.statusUpdatedAt,
      recoveredAttachmentId: reviewedReceiptAttachmentId,
      recoveredFrom:
        receiptRecoveryCase.recoveredFrom ??
        (receiptAttachment?.reviewSource === 'AGENT_EMAIL_SEARCH'
          ? 'GMAIL'
          : receiptAttachment?.reviewSource === 'ORPHAN_RECONCILIATION'
            ? 'ORPHANED_RECEIPT'
            : undefined),
      recoveredReceipt:
        lifecycleState === ReceiptRecoveryLifecycleState.NeedsReview
          ? { submittedAt: receiptRecoveryCase.statusUpdatedAt }
          : undefined,
      reconciledAt:
        lifecycleState === ReceiptRecoveryLifecycleState.ReceiptReconciled
          ? receiptRecoveryCase.statusUpdatedAt
          : undefined,
    }
  }
  if (!mockDataEnabled) return undefined
  if (localPreviewState) return getLocalPreviewCase(localPreviewState)

  return undefined
}

const receiptRecoveryLifecycleStateByStatus: Record<
  string,
  ReceiptRecoveryLifecycleState | undefined
> = {
  NEEDS_REVIEW: ReceiptRecoveryLifecycleState.NeedsReview,
  WAITING_ON_SPENDER: ReceiptRecoveryLifecycleState.WaitingOnSpender,
  MATCHED: ReceiptRecoveryLifecycleState.ReceiptReconciled,
  REVIEW_REJECTED: ReceiptRecoveryLifecycleState.MissingReceipt,
}

export const canSubmitLiveReceiptRecoveryReview = ({
  isMockPreview,
  organizationId,
  receiptRecoveryCase,
  receiptAttachments,
}: CanSubmitLiveReceiptRecoveryReviewParams): boolean => {
  const receiptAttachmentId =
    getReviewedReceiptAttachmentId(receiptRecoveryCase)

  return (
    !isMockPreview &&
    organizationId !== undefined &&
    receiptRecoveryCase?.id !== undefined &&
    receiptRecoveryCase.version !== undefined &&
    receiptAttachmentId !== undefined &&
    receiptAttachments.some(
      (attachment) => attachment.id === receiptAttachmentId,
    )
  )
}
